import { Printer, MapPin, Phone, Mail, Clock, BadgePercent, Sparkles } from "lucide-react";
import { telHref } from "@/lib/scroll";
import { useSettings } from "@/lib/SettingsContext";

export default function FlyerPage() {
  const settings = useSettings();
  const domestic = (settings.services || []).filter((s) => ["domestic", "deep", "tenancy", "oven"].includes(s.id));
  const commercial = (settings.services || []).filter((s) => s.id === "commercial");
  const officeTiers = (settings.extras || []).filter((e) => e.name.toLowerCase().includes("office"));
  const otherExtras = (settings.extras || []).filter((e) => !e.name.toLowerCase().includes("office") && !e.name.toLowerCase().includes("contract"));

  const Row = ({ name, price }) => (
    <div className="flex items-baseline justify-between gap-4 py-2">
      <span className="text-sm text-[#121A16]">{name}</span>
      <span aria-hidden className="mx-1 flex-1 border-b border-dotted border-[#4A5A52]/40" />
      <span className="whitespace-nowrap font-mono text-sm font-semibold text-[#0F382A]">{price}</span>
    </div>
  );

  return (
    <div data-testid="flyer-page" className="min-h-screen bg-[#F3EFEA] py-10 print:bg-white print:py-0">
      <div className="mx-auto max-w-3xl px-6">
        <button
          data-testid="flyer-print-button"
          onClick={() => window.print()}
          className="mb-6 flex items-center gap-2 rounded-full bg-[#0F382A] px-6 py-3 text-sm font-semibold text-[#FAF8F5] transition-colors duration-300 hover:bg-[#1B523F] print:hidden"
        >
          <Printer className="h-4 w-4" strokeWidth={1.75} />
          Print or save as PDF
        </button>

        <div className="overflow-hidden rounded-[2rem] border-2 border-[#D4AF37]/60 bg-[#FAF8F5] shadow-[0_40px_80px_-40px_rgba(11,29,22,0.25)] print:rounded-none print:border-0 print:shadow-none">
          {/* Header */}
          <div className="bg-[#0B1D16] px-10 py-10 text-center">
            <p className="flex items-center justify-center gap-2 font-serif text-4xl font-light text-[#FAF8F5]">
              Don Sparkle
              <Sparkles className="h-6 w-6 text-[#D4AF37]" strokeWidth={1.5} />
            </p>
            <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">
              Cleaning Services · Harlow & Essex
            </p>
            <p className="mx-auto mt-4 max-w-md font-serif text-lg font-light italic text-[#FAF8F5]/70">
              The art of pristine living — meticulous domestic & commercial cleaning
            </p>
          </div>

          {/* Offer */}
          <div className="border-b border-[#E5DEC3] bg-[#F4ECE1] px-10 py-6">
            <div className="flex items-center justify-center gap-3 text-center">
              <BadgePercent className="h-6 w-6 shrink-0 text-[#8a6d1a]" strokeWidth={1.75} />
              <p className="font-serif text-2xl font-light text-[#121A16]">
                {settings.offer?.title}
                <span className="ml-2 text-base italic text-[#4A5A52]">— {settings.offer?.subtitle}</span>
              </p>
            </div>
          </div>

          {/* Prices */}
          <div className="grid grid-cols-1 gap-8 px-10 py-10 sm:grid-cols-2">
            <div>
              <p className="mb-3 border-b-2 border-[#D4AF37] pb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#1B523F]">
                Domestic Cleaning
              </p>
              {domestic.map((s) => (
                <Row key={s.id} name={s.name} price={s.price_label} />
              ))}
              {otherExtras.map((e) => (
                <Row key={e.name} name={e.name} price={e.price_label} />
              ))}
            </div>
            <div>
              <p className="mb-3 border-b-2 border-[#D4AF37] pb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#1B523F]">
                Commercial Cleaning
              </p>
              {commercial.map((s) => (
                <Row key={s.id} name={s.name} price={s.price_label} />
              ))}
              {officeTiers.map((e) => (
                <Row key={e.name} name={e.name} price={e.price_label} />
              ))}
              <div className="mt-4 rounded-xl border border-[#D4AF37]/40 bg-[#F4ECE1] px-4 py-3">
                <p className="text-xs leading-relaxed text-[#4A5A52]">
                  <span className="font-semibold text-[#0F382A]">Weekly & monthly contracts</span> available.
                  Professional invoice & receipt with every job. Fully insured, vetted cleaners.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-[#0B1D16] px-10 py-8">
            <div className="grid grid-cols-1 gap-3 text-center sm:grid-cols-2 sm:text-left">
              <p className="flex items-center justify-center gap-2 text-sm text-[#FAF8F5] sm:justify-start">
                <Phone className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.75} />
                <a href={telHref(settings.phone_display)} className="font-semibold">{settings.phone_display}</a>
                <span className="text-[#FAF8F5]/50">· Call or WhatsApp</span>
              </p>
              <p className="flex items-center justify-center gap-2 text-sm text-[#FAF8F5] sm:justify-start">
                <Mail className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.75} />
                {settings.email}
              </p>
              <p className="flex items-center justify-center gap-2 text-sm text-[#FAF8F5]/70 sm:justify-start">
                <MapPin className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.75} />
                {settings.address}
              </p>
              <p className="flex items-center justify-center gap-2 text-sm text-[#FAF8F5]/70 sm:justify-start">
                <Clock className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.75} />
                {settings.hours}
              </p>
            </div>
            <p className="mt-5 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">
              Covering {(settings.areas || []).join(" · ")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
